/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Description from "App/model/description";
import Projections from "App/utils/projections";
import {logInfo} from "App/debug";
import {vec3} from "gl-matrix";

export default
class GeoLocationSettings {

    private _modelDescription: Description;
    private _properties: any;
    private _EPSG2WKTMap: any;

    constructor(aModelDescription: Description) {

        this._modelDescription = aModelDescription;

        // default values
        this._properties = {
            latitude: 0,
            longitude: 0,
            address: null,
            addressShort: null,
            projectionType: "", // zeropoint transformation contains this projection type,
                                // one can e.g. transform this projection type to another e.g. wgs
            useLatLng: false    // when true, the lattitude and longiture is the origin of the model and the
                                // zeropoint transformation is just offset in meters from this position
        };

        // translate EPSG code to partial wkt
        this._EPSG2WKTMap = {};
        this._EPSG2WKTMap["EPSG:21781"] = "Swiss CH1903 / LV03";
        this._EPSG2WKTMap["EPSG:2056"] = "Swiss CH1903+ / LV95";

        this._EPSG2WKTMap["EPSG:2154"] = "France RGF93 / Lambert-93";
        this._EPSG2WKTMap["EPSG:27561"] = "NTF (Paris) / Lambert Nord France";
        this._EPSG2WKTMap["EPSG:27562"] = "NTF (Paris) / Lambert Centre France";
        this._EPSG2WKTMap["EPSG:27563"] = "NTF (Paris) / Lambert Sud France";
        this._EPSG2WKTMap["EPSG:27564"] = "NTF (Paris) / Lambert Corse";

        this._EPSG2WKTMap["CRS:5243"] = "ETRS89 / LCC Germany (E-N)";
        this._EPSG2WKTMap["EPSG:31466"] = "DHDN / 3-degree Gauss-Kruger zone 2";
        this._EPSG2WKTMap["EPSG:31467"] = "DHDN / 3-degree Gauss-Kruger zone 3";
        this._EPSG2WKTMap["EPSG:31468"] = "DHDN / 3-degree Gauss-Kruger zone 4";
        this._EPSG2WKTMap["EPSG:31469"] = "DHDN / 3-degree Gauss-Kruger zone 5";

        this._EPSG2WKTMap["CRS:5325"] = "ISN2004 / Lambert 2004";
        this._EPSG2WKTMap["EPSG:3038"] = "ETRS89 / TM26";
        this._EPSG2WKTMap["EPSG:3039"] = "ETRS89 / TM27";
        this._EPSG2WKTMap["EPSG:3040"] = "ETRS89 / UTM zone 28N (N-E)";

        this._EPSG2WKTMap["EPSG:26941"] = "NAD83 / California zone 1";
        this._EPSG2WKTMap["EPSG:26942"] = "NAD83 / California zone 2";
        this._EPSG2WKTMap["EPSG:26943"] = "NAD83 / California zone 3";
        this._EPSG2WKTMap["EPSG:26944"] = "NAD83 / California zone 4";
        this._EPSG2WKTMap["EPSG:26945"] = "NAD83 / California zone 5";
        this._EPSG2WKTMap["EPSG:26946"] = "NAD83 / California zone 6";

        this._EPSG2WKTMap["EPSG:4326"] = "WGS 84 (for GPS)";
        this._EPSG2WKTMap["EPSG:32630"] = "WGS 84 / UTM zone 30N";
        this._EPSG2WKTMap["EPSG:32631"] = "WGS 84 / UTM zone 31N";
        this._EPSG2WKTMap["EPSG:32632"] = "WGS 84 / UTM zone 32N";
        this._EPSG2WKTMap["EPSG:32633"] = "WGS 84 / UTM zone 33N";
    }

    public getProperties(): any {
        let props = {};

        for (let key in this._properties) {
            props[key] = this._properties[key];
        }

        return props;
    }

    public getGeoLocationInfo(): any {
        let lat = "";
        let lng = "";
        if (this._properties.useLatLng) {
            lat = this._properties.latitude;
            lng = this._properties.longitude;
        } else if (this._properties.projectionType !== "") {
            let zeropoint = this._modelDescription.getRenderModel().getZeropointSettingsRef().zeropoint;
            let planetPoint = zeropoint._transform(vec3.fromValues(0, 0, 0));
            let res = Projections.proj4(this._properties.projectionType, "EPSG:4326", [planetPoint[0], planetPoint[2]]);
            if (res !== undefined) {
                lng = res[0];
                lat = res[1];
            }
        }

        return {
            latitude: lat,
            longitude: lng,
            projectionType: {
                EPSG: this._properties.projectionType,
                WKT: this._properties.projectionType !== "" ? this._EPSG2WKTMap[this._properties.projectionType] : ""
            },
            units: this._properties.useLatLng ? ["Lng", "Alt", "Lat"] : ["x", "y", "z"]
        };
    }

    public parse(aModelDescriptionJSON: any): boolean {
        if (!aModelDescriptionJSON.hasOwnProperty("GeoLocationSettings")) {
            return false;
        }

        for (let key in aModelDescriptionJSON.GeoLocationSettings) {
            if (this._properties[key] !== undefined) {
                this._properties[key] = aModelDescriptionJSON.GeoLocationSettings[key];
            } else {
                logInfo("Error: property '" + key + "' is nor member of the geo location settings.");
            }
        }

        return true;
    }

    public getJSONRepresentation(): any {
        return this.getProperties();
    }
}
